<script lang="ts" setup>
import { openCustomerService } from '../openCustomerService'
defineOptions({ name: 'PartnersPage' })

import { reactive } from 'vue'
import { Icon } from '@/components/Icon'
import { partnerTiers, partnerBenefits } from '../data/portalData'
import { ElMessage } from 'element-plus'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'
import tierPolicyImage from '@/assets/portal/partners/tier-policy.png'



const form = reactive({
  company: '',
  name: '',
  phone: '',
  level: '',
  region: '',
  note: ''
})

const levelOptions = partnerTiers.map((t) => {
  const priceShort = t.price.replace('预存 ', '').replace(' 元', '')
  const discountShort = t.discount.replace('终身拿货 ', '').replace('所有产品', '')
  return {
    label: `${priceShort} ${t.level}${t.name}（${discountShort}）`,
    value: t.level
  }
})

// FAQ state
const expandedFaqs = reactive<Record<number, boolean>>({})
function toggleFaq(idx: number) {
  expandedFaqs[idx] = !expandedFaqs[idx]
}

const partnerFaqs = [
  {
    question: '代理权限、产品授权是否终身有效？',
    answer:
      '全部终身有效，无年费、无有效期限制；预存款永久不清零，产品拿货均为终身授权，一次投入，终身受益。'
  },
  {
    question: '下级代理缴纳的预存款是否全部归上级？平台是否抽成？',
    answer:
      '下级所有预存款、拿货消费资金<span style="color:#0066FF;font-weight:600">100%归属上级代理</span>，平台不抽取任何差价、不参与分润，全部纯利润归你所有。'
  },
  {
    question: '地域保护机制是否严格，会不会出现跨区抢客？',
    answer:
      '系统自动识别客户归属区域，跨区域客户自动分流至对应代理，从<span style="color:#0066FF;font-weight:600">系统底层杜绝抢客行为</span>，你的区域客源绝对安全。'
  },
  {
    question: '代理是否可以单独售卖模块源码、做私有化部署交付？',
    answer:
      '可以。所有业务模块独立解耦，支持单独交付客户；底层统一加密Jar包保障平台核心代码安全，<span style="color:#0066FF;font-weight:600">高溢价空间</span>远超普通SaaS零售。'
  },
  {
    question: '代理后期想升级更高档位如何操作？',
    answer:
      '仅需<span style="color:#0066FF;font-weight:600">补齐对应档位差额</span>预存款即可升级，升级后自动更新拿货折扣、扩大地域保护范围，无缝衔接更高收益。'
  }
]

async function handleSubmit() {
  if (!form.company || !form.name || !form.phone) {
    ElMessage.warning('请填写公司名称、姓名和联系电话')
    return
  }
  try {
    await submitPortalForm({
      username: form.name,
      companyName: form.company,
      tel: form.phone,
      address: form.region,
      content: form.note,
      productName: form.level,
      type: 'PARTNER_APPLICATION'
    })
    ElMessage.success('申请已提交！招商顾问将在24小时内联系您')
    Object.assign(form, { company: '', name: '', phone: '', level: '', region: '', note: '' })
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<template>
  <div class="partners-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="hero-overlay" />
      <div class="hero-content">
        <h1 class="hero-title">区域独家代理招商加盟</h1>
        <p class="hero-subtitle">
          零年费 · 终身拿货 · 区域独家垄断代理<br />紫金山AI SaaS全平台独家招商
        </p>
        <div class="hero-actions">
          <!-- 申请表单已隐藏，暂去掉锚点按钮
          <a href="#partner-apply" class="hero-btn-primary">立即抢占区域名额</a>
          -->
          <a href="#partner-level" class="hero-btn-outline">查看代理政策</a>
        </div>
      </div>
    </div>

    <div class="content-wrap">
      <!-- Why choose us -->
      <section class="benefit-section">
        <div class="section-header">
          <h2 class="section-title">为什么选择紫金山AI代理？</h2>
          <div class="section-bar" />
        </div>
        <div class="benefit-grid">
          <div v-for="item in partnerBenefits" :key="item.title" class="benefit-card card-hover">
            <div class="benefit-icon-wrap" :style="{ background: item.bgColor }">
              <Icon :icon="item.icon" :size="26" :style="{ color: item.color }" />
            </div>
            <h4 class="benefit-title">{{ item.title }}</h4>
            <p class="benefit-desc">{{ item.desc }}</p>
          </div>
        </div>
      </section>

      <!-- Agent Tiers -->
      <section id="partner-level" class="tier-section">
        <div class="section-header">
          <h2 class="section-title">多梯度代理 · 终身拿货折扣</h2>
          <div class="section-bar" />
          <p class="section-desc">
            所有插件、模板、中台系统、营销工具统一终身授权，档位越高利润空间、区域权限越大
          </p>
        </div>
        <div class="tier-grid">
          <img
            :src="tierPolicyImage"
            class="tier-policy-image"
            alt="多梯度代理终身拿货折扣政策"
            loading="lazy"
            style="cursor: pointer"
            @click="openCustomerService"
          />
        </div>
      </section>

      <!-- 五大独家核心代理优势 -->
      <section class="advantage-section">
        <div class="section-header">
          <h2 class="section-title">五大独家核心代理优势</h2>
          <div class="section-bar" />
          <p class="section-desc">每一重优势都是行业壁垒，无法复制的竞争护城河</p>
        </div>

        <div class="advantage-list">
          <!-- 01 终身拿货 -->
          <div class="advantage-card card-hover">
            <div
              class="advantage-side"
              style="background: linear-gradient(135deg, #0066ff, #0044cc)"
            >
              <div class="advantage-watermark">01</div>
              <div class="advantage-side-inner">
                <div class="advantage-icon-wrap">
                  <Icon icon="lucide:infinity" :size="30" color="#fff" />
                </div>
                <p class="advantage-side-label">持久 · 稳固</p>
              </div>
            </div>
            <div class="advantage-body">
              <div class="advantage-meta">
                <span class="advantage-num advantage-num-blue">01</span>
                <span class="advantage-tag">终身拿货模式，市面独有</span>
              </div>
              <h3 class="advantage-title">一次拿货，永久售卖无续期成本</h3>
              <p class="advantage-desc">
                市面上绝大多数SaaS平台均按年收取授权费，客户每年续费成本高、代理复购利润薄。紫金山AI全部产品统一<span
                  class="text-brand"
                  >终身授权</span
                >，代理一次采购可永久对外售卖，无年费、无隐形续费，长期收益稳定。
              </p>
            </div>
          </div>

          <!-- 02 地域保护 (reverse) -->
          <div class="advantage-card card-hover advantage-reverse">
            <div
              class="advantage-side"
              style="background: linear-gradient(135deg, #7c3aed, #a78bfa)"
            >
              <div class="advantage-watermark">02</div>
              <div class="advantage-side-inner">
                <div class="advantage-icon-wrap">
                  <Icon icon="lucide:map-pinned" :size="30" color="#fff" />
                </div>
                <p class="advantage-side-label">独占 · 排他</p>
              </div>
            </div>
            <div class="advantage-body">
              <div class="advantage-meta">
                <span class="advantage-num advantage-num-purple">02</span>
                <span class="advantage-tag">严格地域独家保护锁客</span>
              </div>
              <h3 class="advantage-title">省/市/区专属授权，杜绝跨区抢客</h3>
              <p class="advantage-desc">
                系统根据客户营业执照、IP、联系地址<span class="text-purple">自动判定归属区域</span
                >，其他代理无法抢占你的客户。同一区域仅存在一名同级代理商，区域内所有租户、下级代理全部归你管辖，客源稳定不流失。
              </p>
            </div>
          </div>

          <!-- 03 下级资金归上级 -->
          <div class="advantage-card card-hover">
            <div
              class="advantage-side"
              style="background: linear-gradient(135deg, #00b96b, #0891b2)"
            >
              <div class="advantage-watermark">03</div>
              <div class="advantage-side-inner">
                <div class="advantage-icon-wrap">
                  <Icon icon="lucide:percent" :size="30" color="#fff" />
                </div>
                <p class="advantage-side-label">100% · 归属</p>
              </div>
            </div>
            <div class="advantage-body">
              <div class="advantage-meta">
                <span class="advantage-num advantage-num-green">03</span>
                <span class="advantage-tag">下级全部资金100%归属上级</span>
              </div>
              <h3 class="advantage-title">平台不抽一分差价，全部纯利归你</h3>
              <p class="advantage-desc">
                你自主招募的二级代理缴纳的所有门槛预存款、后续全部拿货消费资金，<span
                  class="text-green"
                  >全额计入你的代理账户</span
                >，平台不参与任何分成。你可自由自定义下级拿货折扣，差价全部为你的纯利润。
              </p>
            </div>
          </div>

          <!-- 04 模块化源码交付 (reverse) -->
          <div class="advantage-card card-hover advantage-reverse">
            <div
              class="advantage-side"
              style="background: linear-gradient(135deg, #f59e0b, #e11d48)"
            >
              <div class="advantage-watermark">04</div>
              <div class="advantage-side-inner">
                <div class="advantage-icon-wrap">
                  <Icon icon="lucide:code-2" :size="30" color="#fff" />
                </div>
                <p class="advantage-side-label">解耦 · 灵活</p>
              </div>
            </div>
            <div class="advantage-body">
              <div class="advantage-meta">
                <span class="advantage-num advantage-num-amber">04</span>
                <span class="advantage-tag">模块化源码交付，高溢价空间</span>
              </div>
              <h3 class="advantage-title">核心底层加密Jar包隔离，单模块独立售卖</h3>
              <p class="advantage-desc">
                平台底层内核全部封装加密Jar包不对外泄露，每个业务模块<span class="text-amber"
                  >独立微服务、独立部署</span
                >。可单独向企业客户出售指定模块源码，私有化定制溢价空间远高于普通SaaS零售。
              </p>
            </div>
          </div>

          <!-- 05 广告互通生态 -->
          <div class="advantage-card card-hover">
            <div
              class="advantage-side"
              style="background: linear-gradient(135deg, #0891b2, #0044cc)"
            >
              <div class="advantage-watermark">05</div>
              <div class="advantage-side-inner">
                <div class="advantage-icon-wrap">
                  <Icon icon="lucide:trending-up" :size="30" color="#fff" />
                </div>
                <p class="advantage-side-label">躺赚 · 持续</p>
              </div>
            </div>
            <div class="advantage-body">
              <div class="advantage-meta">
                <span class="advantage-num advantage-num-cyan">05</span>
                <span class="advantage-tag">跨租户广告互通，长期被动收益</span>
              </div>
              <h3 class="advantage-title">旗下租户商品投放广告，持续分润躺赚</h3>
              <p class="advantage-desc">
                租户自有商品可付费投放至其他租户站点广告位，产生的广告服务费按规则分给区域代理。区域租户越多，广告流水越高，形成<span
                  class="text-cyan"
                  >不依赖卖系统的持续性被动收益</span
                >。
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- 五大稳定盈利渠道 -->
      <section class="profit-section">
        <div class="section-header">
          <h2 class="section-title">五大稳定盈利渠道</h2>
          <div class="section-bar" />
        </div>
        <div class="profit-grid">
          <div class="profit-card">
            <div class="profit-num profit-num-blue">01</div>
            <h3 class="profit-title">产品拿货差价利润</h3>
            <p class="profit-desc"
              >最低1折拿货，原价对外零售，单套产品最高90%毛利，一次性高额收益。</p
            >
          </div>
          <div class="profit-card">
            <div class="profit-num profit-num-green">02</div>
            <h3 class="profit-title">招募下级代理门槛收益</h3>
            <p class="profit-desc"
              >下级缴纳全部预存门槛费全额归你，无平台抽成，批量招商收益可观。</p
            >
          </div>
          <div class="profit-card">
            <div class="profit-num profit-num-amber">03</div>
            <h3 class="profit-title">下级长期拿货差价</h3>
            <p class="profit-desc">下级每次采购产品自动产生差价，持续复购带来稳定流水收益。</p>
          </div>
          <div class="profit-card">
            <div class="profit-num profit-num-purple">04</div>
            <h3 class="profit-title">区域租户全生命周期收益</h3>
            <p class="profit-desc">区域内所有租户购买插件、模板、增值服务，全部与你收益挂钩。</p>
          </div>
          <div class="profit-card profit-card-wide">
            <div class="profit-num profit-num-cyan">05</div>
            <h3 class="profit-title">租户跨平台广告投放分润</h3>
            <p class="profit-desc"
              >租户商品广告投放消耗产生服务费，长期持续性被动收益，不受系统售卖淡旺季影响。</p
            >
          </div>
        </div>
      </section>

      <!-- 平台核心技术实力保障 -->
      <section class="tech-section">
        <div class="section-header">
          <h2 class="section-title">平台核心技术实力保障</h2>
          <div class="section-bar" />
        </div>
        <div class="tech-grid">
          <div class="tech-card">
            <ul class="tech-list">
              <li
                ><span class="tech-bullet">●</span>
                微服务模块化拆分，各模块支持独立部署、独立迭代交付</li
              >
              <li
                ><span class="tech-bullet">●</span>
                代码分级隔离管控，研发仅可访问自身板块，核心底层加密Jar不外流</li
              >
              <li
                ><span class="tech-bullet">●</span>
                一租户一独立数据库，数据物理隔离，平台统一备份运维管理</li
              >
              <li
                ><span class="tech-bullet">●</span>
                租户资源独立统计计量，预留按量计费体系，后期增值收费</li
              >
            </ul>
          </div>
          <div class="tech-card">
            <ul class="tech-list">
              <li
                ><span class="tech-bullet">●</span>
                租户全自主配置中心：域名、短信、邮箱、通知、SEO、存储、支付</li
              >
              <li
                ><span class="tech-bullet">●</span>
                完整广告互通生态，租户私域商品跨站点合规投放推广</li
              >
              <li
                ><span class="tech-bullet">●</span>
                总平台/代理/租户三套独立后台，统一等级、折扣、到期资产体系</li
              >
              <li
                ><span class="tech-bullet">●</span>
                插件市场、模板中心、业务中台、营销工具全产品线覆盖</li
              >
            </ul>
          </div>
        </div>
      </section>

      <!-- 代理常见问答 -->
      <section class="faq-section">
        <div class="section-header">
          <h2 class="section-title">代理常见问答</h2>
          <div class="section-bar" />
          <p class="section-desc">关于代理政策你最关心的问题，都在这里</p>
        </div>
        <div class="faq-list">
          <div v-for="(item, idx) in partnerFaqs" :key="idx" class="faq-item">
            <div class="faq-question" @click="toggleFaq(idx)">
              <div class="faq-q-inner">
                <span class="faq-q-num">Q{{ idx + 1 }}</span>
                <span class="faq-q-text">{{ item.question }}</span>
              </div>
              <Icon
                icon="lucide:chevron-down"
                :size="16"
                class="faq-chevron"
                :class="{ 'faq-chevron-open': expandedFaqs[idx] }"
              />
            </div>
            <div class="faq-answer" :class="{ 'faq-answer-open': expandedFaqs[idx] }">
              <p class="faq-a-text" v-html="item.answer" />
            </div>
          </div>
        </div>
      </section>

      <!-- Application Form（临时隐藏）
      <section id="partner-apply" class="apply-section">
        <div class="apply-overlay" />
        <div class="apply-inner">
          <div class="apply-header">
            <h2 class="apply-title">抢占城市独家代理名额</h2>
            <div class="apply-bar" />
            <p class="apply-desc">
              每个城市仅限一名独家代理，先到先得。填写信息，招商顾问一对一为您定制专属方案
            </p>
          </div>

          <div class="apply-grid">
            <div class="apply-benefits">
              <div class="apply-benefits-card">
                <div class="apply-benefits-label">提交申请即可获得</div>
                <div class="apply-benefits-list">
                  <div class="apply-benefits-item">&#10003; 一对一专属招商顾问全程对接</div>
                  <div class="apply-benefits-item">&#10003; 定制化收益测算与回报分析</div>
                  <div class="apply-benefits-item">&#10003; 区域保护范围与政策详细解读</div>
                  <div class="apply-benefits-item">&#10003; 全套产品演示与私有化方案评估</div>
                  <div class="apply-benefits-item">&#10003; 优先锁定目标城市独家代理资格</div>
                </div>
              </div>
              <div class="apply-hotline">
                <p>全国招商咨询热线</p>
                <p class="apply-hotline-number">400-646-5288</p>
              </div>
            </div>

            <div class="apply-form-card">
              <div class="form-group">
                <label class="form-label">公司名称</label>
                <input v-model="form.company" class="form-input" placeholder="请输入公司全称" />
              </div>
              <div class="form-row-2">
                <div class="form-group">
                  <label class="form-label">您的姓名</label>
                  <input v-model="form.name" class="form-input" placeholder="请输入姓名" />
                </div>
                <div class="form-group">
                  <label class="form-label">联系电话</label>
                  <input
                    v-model="form.phone"
                    class="form-input"
                    type="tel"
                    placeholder="请输入手机号"
                  />
                </div>
              </div>
              <div class="form-row-2">
                <div class="form-group">
                  <label class="form-label">意向代理档位</label>
                  <select v-model="form.level" class="form-input form-select">
                    <option value="">请选择意向档位</option>
                    <option v-for="opt in levelOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">所在区域</label>
                  <input v-model="form.region" class="form-input" placeholder="例：广东省深圳市" />
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">备注需求 <span class="form-optional">(选填)</span></label>
                <textarea
                  v-model="form.note"
                  class="form-input form-textarea"
                  rows="2"
                  placeholder="简单说明自身渠道、资源等"
                />
              </div>
              <button class="form-submit-btn" @click="handleSubmit">
                提交申请，获取专属招商方案
              </button>
              <p class="form-hint">提交后24小时内，专属顾问将与您联系</p>
            </div>
          </div>
        </div>
      </section>
      -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hero-banner {
  height: 420px;
  margin-top: 0;
  background: linear-gradient(135deg, #001b5e, #0066ff);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.08), transparent 70%);
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: #fff;
  max-width: 900px;
  padding: 0 24px;
}

.hero-title {
  font-size: 42px;
  font-weight: 900;
  margin-bottom: 16px;
}

.hero-subtitle {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;
}

.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
}

.hero-btn-primary {
  background: #ffb800;
  color: #111827;
  padding: 14px 32px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 15px;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 184, 0, 0.4);
  }
}

.hero-btn-outline {
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  padding: 14px 32px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.content-wrap {
  padding: 0 24px;
  max-width: 1280px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
}

.section-bar {
  width: 48px;
  height: 3px;
  background: #0066ff;
  border-radius: 2px;
  margin: 0 auto;
}

.section-desc {
  color: #6b7280;
  font-size: 15px;
  margin-top: 8px;
}

// Benefit section
.benefit-section {
  padding: 64px 0;
}

.benefit-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
}

.benefit-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px 20px;
  text-align: center;
}

.benefit-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.benefit-title {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 8px;
}

.benefit-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

// Tier section
.tier-section {
  padding: 64px 0;
  background: #f9fafb;
  margin: 0 -24px;
  padding-left: 24px;
  padding-right: 24px;
}

.tier-grid {
  max-width: 1200px;
  margin: 0 auto;
}

.tier-policy-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.tier-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-top: 4px solid #d1d5db;
  border-radius: 16px;
  padding: 32px 24px;
  position: relative;
  transition: all 0.3s ease;

  &.tier-highlighted {
    background: #1f2937;
    border: 2px solid #ffb800;
    color: #fff;
  }
}

.tier-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 10px;
  font-weight: 600;
}

.tier-badge-hot {
  background: #0066ff;
  color: #fff;
}

.tier-badge-recommend {
  background: #ffb800;
  color: #111827;
}

.tier-level-gold {
  color: #ffb800;
  font-weight: 600;
}

.tier-discount-gold {
  color: #ffb800;
}

.tier-check-gold {
  color: #ffb800;
}

.tier-level {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 8px;

  .tier-highlighted & {
    color: #ffb800;
    font-weight: 600;
  }
}

.tier-price {
  font-size: 28px;
  font-weight: 800;
  margin-bottom: 12px;
}

.tier-discount {
  font-size: 20px;
  font-weight: 700;
  color: #0066ff;
  margin-bottom: 20px;

  .tier-highlighted & {
    color: #ffb800;
  }
}

.tier-features {
  list-style: none;
  font-size: 13px;
  color: #4b5563;
  line-height: 2;
  margin-bottom: 24px;

  .tier-highlighted & {
    color: rgba(255, 255, 255, 0.8);
  }
}

.tier-check {
  color: #00b96b;
  font-weight: 700;

  .tier-highlighted & {
    color: #ffb800;
  }
}

.tier-btn {
  display: block;
  text-align: center;
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
  border: 1px solid #0066ff;
  color: #0066ff;
  transition: all 0.3s ease;

  &:hover {
    background: #0066ff;
    color: #fff;
  }

  &.tier-btn-solid {
    background: #ffb800;
    color: #1f2937;
    border: none;

    &:hover {
      opacity: 0.85;
    }
  }
}

// ======== 五大独家核心代理优势 ========
.advantage-section {
  padding: 64px 0;
}

.advantage-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.advantage-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
}

.advantage-reverse {
  flex-direction: row-reverse;
}

.advantage-side {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 40px 20px;
}

.advantage-watermark {
  position: absolute;
  font-size: 160px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.08);
  line-height: 1;
  pointer-events: none;
}

.advantage-side-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  color: #fff;
}

.advantage-icon-wrap {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.advantage-side-label {
  font-size: 13px;
  opacity: 0.8;
}

.advantage-body {
  flex: 1;
  padding: 32px;
}

.advantage-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.advantage-num {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.advantage-num-blue {
  background: #ebf5ff;
  color: #0066ff;
}

.advantage-num-purple {
  background: #f3e8ff;
  color: #7c3aed;
}

.advantage-num-green {
  background: #ecfdf5;
  color: #00b96b;
}

.advantage-num-amber {
  background: #fffbeb;
  color: #f59e0b;
}

.advantage-num-cyan {
  background: #ecfeff;
  color: #0891b2;
}

.advantage-tag {
  color: #ffb800;
  font-weight: 700;
  font-size: 12px;
}

.advantage-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
}

.advantage-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.8;
}

.text-brand {
  color: #0066ff;
  font-weight: 600;
}

.text-purple {
  color: #7c3aed;
  font-weight: 600;
}

.text-green {
  color: #00b96b;
  font-weight: 600;
}

.text-amber {
  color: #f59e0b;
  font-weight: 600;
}

.text-cyan {
  color: #0891b2;
  font-weight: 600;
}

// ======== 五大稳定盈利渠道 ========
.profit-section {
  padding: 64px 0;
  background: #f9fafb;
  margin: 0 -24px;
  padding-left: 24px;
  padding-right: 24px;
}

.profit-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.profit-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px;
}

.profit-card-wide {
  grid-column: span 2;
}

.profit-num {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 16px;
}

.profit-num-blue {
  background: #ebf5ff;
  color: #0066ff;
}

.profit-num-green {
  background: #ecfdf5;
  color: #00b96b;
}

.profit-num-amber {
  background: #fffbeb;
  color: #f59e0b;
}

.profit-num-purple {
  background: #f3e8ff;
  color: #7c3aed;
}

.profit-num-cyan {
  background: #ecfeff;
  color: #0891b2;
}

.profit-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
}

.profit-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}

// ======== 平台核心技术实力保障 ========
.tech-section {
  padding: 64px 0;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.tech-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px;
}

.tech-list {
  list-style: none;
  font-size: 14px;
  color: #4b5563;
  line-height: 2.2;
  display: flex;
  flex-direction: column;
  gap: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
}

.tech-bullet {
  color: #0066ff;
  flex-shrink: 0;
  margin-top: 2px;
}

// ======== 代理常见问答 ========
.faq-section {
  padding: 64px 0;
  background: #f9fafb;
  margin: 0 -24px;
  padding-left: 24px;
  padding-right: 24px;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
}

.faq-question {
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.faq-q-inner {
  display: flex;
  align-items: center;
  gap: 12px;
}

.faq-q-num {
  width: 32px;
  height: 32px;
  background: #0066ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.faq-q-text {
  font-weight: 600;
  font-size: 14px;
}

.faq-chevron {
  color: #9ca3af;
  transition: transform 0.3s;
  flex-shrink: 0;
}

.faq-chevron-open {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.faq-answer-open {
  max-height: 200px;
}

.faq-a-text {
  padding: 0 24px 18px 68px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.8;
}
.apply-section {
  padding: 64px 0;
  background: linear-gradient(135deg, #001b5e, #0066ff);
  margin: 0 -24px;
  padding-left: 24px;
  padding-right: 24px;
  position: relative;
  overflow: hidden;
}

.apply-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.06), transparent 60%);
}

.apply-inner {
  position: relative;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto;
}

.apply-header {
  text-align: center;
  margin-bottom: 40px;
  color: #fff;
}

.apply-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
}

.apply-bar {
  width: 48px;
  height: 3px;
  background: #ffb800;
  border-radius: 2px;
  margin: 0 auto 12px;
}

.apply-desc {
  opacity: 0.8;
  font-size: 15px;
  max-width: 600px;
  margin: 0 auto;
}

.apply-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 32px;
}

.apply-benefits-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
  color: #fff;
}

.apply-benefits-label {
  font-size: 13px;
  color: #ffb800;
  font-weight: 600;
  margin-bottom: 12px;
}

.apply-benefits-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
  opacity: 0.85;
  line-height: 1.7;
}

.apply-benefits-item::before {
  content: '\2713';
  color: #ffb800;
  margin-right: 8px;
}

.apply-hotline {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin-top: 16px;
}

.apply-hotline-number {
  font-size: 18px;
  font-weight: 700;
  color: #ffb800;
  margin-top: 4px;
}

.apply-form-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 32px;
}

.form-group {
  margin-bottom: 16px;
}

.form-row-2 .form-group {
  margin-bottom: 0;
}

.form-group:has(.form-textarea) {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  margin-bottom: 6px;
}

.form-optional {
  opacity: 0.5;
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 14px;
  color: #fff;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
}

.form-select {
  appearance: none;
}

.form-textarea {
  height: auto;
  padding: 12px 14px;
  resize: none;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-submit-btn {
  width: 100%;
  height: 48px;
  background: #ffb800;
  color: #1f2937;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.85;
  }
}

.form-hint {
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  margin-top: 12px;
}

.card-hover {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }
}

// Responsive
@media (max-width: 1024px) {
  .benefit-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .apply-grid {
    grid-template-columns: 1fr;
  }

  .advantage-card {
    flex-direction: column;
  }

  .advantage-side {
    width: 100%;
    padding: 32px 20px;
  }

  .profit-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tech-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .benefit-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-row-2 {
    grid-template-columns: 1fr;
  }

  .profit-grid {
    grid-template-columns: 1fr;
  }

  .profit-card-wide {
    grid-column: span 1;
  }

  .advantage-card {
    flex-direction: column;
  }

  .advantage-side {
    width: 100%;
  }
}
</style>
